import { faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Snackbar from "../../common/Snackbar";
import Loader from "../../common/Loader";
import {
  changePassword,
  registerUser,
  updateUser,
} from "../../redux/authSlice";
import { generateSHA256 } from "../../util/generateSHA256";
import SignatureCanvas from "react-signature-canvas";
import { getRevisionsByUser } from "../../redux/procedureRevisionSlice";
import { changeUserStatus, fetchUsers } from "../../redux/userSlice";
import { fetchTasksByUserId } from "../../redux/taskSlice";
import { BASE_URL } from "../../config";
import { getAllCategories } from "../../redux/categorySlice";
import {
  TextField,
  TextArea,
  CheckboxField,
  SignatureField,
  RadioButton,
  InlineCheckbox,
} from "../../common/FormFields";
import "./UserForm.css";
import { buildPermissionContext } from "../../util/permissions";
import { typeLabels } from "../Products/TypeLabels";
import DisableUserTaskPopup from "../Tasks/DisableUserTaskPopup";
import {
  getAuditTrailData,
  getFieldsIncluded,
} from "../../util/getAuditTrailData";
import { getLoggedInUserInfo, uploadJsonToS3 } from "../../util/AppUtils";

const editModeOptions = [
  "No Access",
  "All Edit Mode Permissions",
  "Admin",
  "Superadmin",
];

const permissionCheckboxes = [
  "View",
  "Edit",
  "Create",
  "Submit",
  "Approve",
  "Release",
  "Archive",
  "Reject",
  "Reassign",
  "Import/Export",
];

const getInitialRecordPermissions = () =>
  permissionCheckboxes.reduce((acc, perm) => {
    acc[perm] = false;
    return acc;
  }, {});

const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    loading,
    error,
    user: currentAuthUser,
  } = useSelector((state) => state.auth);
  const { revisionsByUserData, loading: revisionsLoading } = useSelector(
    (state) => state.procedureRevision,
  );
  const { users: storeUsers } = useSelector((state) => state.users);
  const { modules: products } = useSelector((state) => state.module);
  const { categories } = useSelector((state) => state.category);
  const moduleOptions = React.useMemo(
    () => [
      ...Array.from(
        new Set(products.map((p) => p.module_part?.type_name).filter(Boolean)),
      ),
      "Not Assigned",
    ],
    [products],
  );
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const isEditMode = location.state?.editMode || false;
  const existingUser = location.state?.user || {};

  // Add state for the disable user popup
  const [isDisableTaskPopupOpen, setIsDisableTaskPopupOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [disableUserData, setDisableUserData] = useState({
    proceduresToChange: [],
    assignChangeRequestTo: "",
    changeRequestComments: "",
    reassignOpenTasksTo: "",
    disabledBy: localStorage.getItem("user_name") || "",
    password: "",
    signature: "",
  });
  const sigPadRef = useRef();

  // Get signature auth methods from localStorage
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);

  const permissionCtx = buildPermissionContext(currentAuthUser);
  //const canEdit = permissionCtx.permissions?.Edit === true;
  const canEdit = true;

  // Check if logged-in user is allowed to change passwords
  // Get email from Redux state first, fallback to localStorage (consistent with Login.js)
  const loggedInUserEmail =
    currentAuthUser?.email || getLoggedInUserInfo()?.email || "";
  const allowedUserEmails = process.env.REACT_APP_ALLOWED_USER_EMAILS || "";

  // Restrict user management to specific emails only - read from environment variable
  const getAllowedEmails = () => {
    const envEmails = process.env.REACT_APP_ALLOWED_USER_EMAILS;
    if (envEmails) {
      return envEmails.split(",").map((email) => email.trim().toLowerCase());
    }
    // Fallback to default emails if env variable is not set
    return ["sahil@rushabhinstruments.com", "preyas@rushabhinstruments.com"];
  };
  const allowedEmails = getAllowedEmails();
  const allowedEmailsList = allowedUserEmails
    .split(",")
    .map((email) => email.trim());
  const canChangePassword =
    isEditMode &&
    loggedInUserEmail &&
    allowedEmailsList.includes(loggedInUserEmail);
  const isAuthorizedUser = allowedEmails.includes(
    currentAuthUser?.email?.toLowerCase(),
  );
  // const canManageUsers = !isEditMode || (canEdit && isAuthorizedUser);
  const canManageUsers = true;
  const shouldDisablePasswordFields = isEditMode && !isAuthorizedUser;

  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  });

  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    status: "active",
    role: "",
    user_information: "",
    note: "",
    module_type_permissions: {
      QMS: { view: false, edit: false },
      // other modules will follow the same format
    },
    permission_to_edit: "No Access",
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisablingUser, setIsDisablingUser] = useState(false);

  // Add state for procedures data
  const [proceduresData, setProceduresData] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [approvedPartsList, setApprovedPartsList] = useState([]);
  const [recordPermissionsState, setRecordPermissionsState] = useState({
    mode: "none",
    selections: getInitialRecordPermissions(),
  });
  const [moduleLiveMode, setModuleLiveMode] = useState("select");
  const [moduleEditMode, setModuleEditMode] = useState("select");
  const canAssignAdmin =
    currentAuthUser?.permission_to_edit?.SuperAdmin === true;

  // Filter categories for "Users > Role"
  const roleCategories = React.useMemo(() => {
    return categories.filter((cat) => cat.field_name === "Users > Role");
  }, [categories]);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(getAllCategories({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode) {
      // Ensure permission_to_edit is properly formatted for edit mode
      let permissionToEdit = existingUser.permission_to_edit || "No Access";
      // If permission_to_edit is an object, set View based on existing value or default to true
      if (typeof permissionToEdit === "object" && permissionToEdit !== null) {
        // Only set View to true if it's not explicitly false (preserve existing state)
        if (permissionToEdit.View === undefined) {
          permissionToEdit = { ...permissionToEdit, View: true };
        }
      }

      // Initialize role from role_categories array
      let roleValue = "";
      if (
        existingUser.role_categories &&
        Array.isArray(existingUser.role_categories) &&
        existingUser.role_categories.length > 0
      ) {
        // Extract category names from role_categories array
        const roleNames = existingUser.role_categories
          .map((role) => role.category_name)
          .filter(Boolean);
        if (roleNames.length > 0) {
          roleValue = roleNames.join(", ");
        }
      }

      // If SuperAdmin is checked, role should be "All"
      const isSuperAdmin =
        typeof permissionToEdit === "object" &&
        permissionToEdit !== null &&
        permissionToEdit.SuperAdmin === true;

      const userDataForEdit = {
        ...existingUser,
        permission_to_edit: permissionToEdit,
        role: isSuperAdmin ? "All" : roleValue || existingUser.role || "",
      };
      setUserData(userDataForEdit);
      const permissions = userDataForEdit.module_type_permissions || {};
      const liveAllSelected = moduleOptions.every(
        (module) => permissions?.[module]?.view,
      );
      const editAllSelected = moduleOptions.every(
        (module) => permissions?.[module]?.edit,
      );
      setModuleLiveMode(liveAllSelected ? "all" : "select");
      setModuleEditMode(editAllSelected ? "all" : "select");
      // Clear password error in edit mode
      setPasswordError("");
    } else {
      // Clear password error when creating new user
      setPasswordError("");
    }
  }, [isEditMode, existingUser]);

  useEffect(() => {
    if (isEditMode) {
      // Use livemode_permissions_to_edit instead of record_permissions
      const existingPermissions = {
        ...getInitialRecordPermissions(),
        ...(existingUser.livemode_permissions_to_edit || {}),
      };
      const allSelected = permissionCheckboxes.every(
        (perm) => existingPermissions[perm],
      );
      const noneSelected = permissionCheckboxes.every(
        (perm) => !existingPermissions[perm],
      );
      const mode = allSelected ? "all" : noneSelected ? "none" : "select";
      // Ensure View is always true when mode is "select" or "all"
      const finalPermissions = {
        ...existingPermissions,
        View: mode !== "none" ? true : existingPermissions.View,
      };
      setRecordPermissionsState({
        mode,
        selections: finalPermissions,
      });
    }
  }, [isEditMode, existingUser]);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip })),
      )
      .catch((err) => console.error("Failed to fetch IP:", err));
  }, []);

  // Fetch procedures when disable modal opens
  useEffect(() => {
    if (isDisableModalOpen && existingUser.id) {
      dispatch(getRevisionsByUser({ userId: existingUser.id }));
    }
  }, [isDisableModalOpen, existingUser.id, dispatch]);

  // Update procedures data when API response comes
  useEffect(() => {
    if (revisionsByUserData && isDisableModalOpen) {
      const groups = revisionsByUserData.data || revisionsByUserData.results;
      if (Array.isArray(groups) && groups.length > 0) {
        // If grouped shape with parts_with_rev exists
        if (groups[0]?.parts_with_rev !== undefined) {
          const approveGroup = groups.find(
            (g) => (g.purpose || "").toLowerCase() === "approve",
          );
          if (approveGroup?.parts_with_rev) {
            const list = approveGroup.parts_with_rev
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            // Remove duplicates using Set
            const uniqueList = Array.from(new Set(list));
            setApprovedPartsList(uniqueList);
          } else {
            setApprovedPartsList([]);
          }
        } else {
          // Fallback for ungrouped shape (array of revisions)
          setProceduresData(groups);
          const list = groups
            .filter((row) => (row.purpose || "").toLowerCase() === "approve")
            .map(
              (row) => `${row.part_number || "N/A"} Rev ${row.rev || "N/A"}`,
            );
          setApprovedPartsList(list);
        }
      } else {
        setProceduresData([]);
        setApprovedPartsList([]);
      }
    }
  }, [revisionsByUserData, isDisableModalOpen]);

  // Fetch users list for assignment using redux
  useEffect(() => {
    if (isDisableModalOpen) {
      dispatch(fetchUsers({ limit: 1000, offset: 0 }));
    }
  }, [isDisableModalOpen, dispatch]);

  useEffect(() => {
    if (Array.isArray(storeUsers) && storeUsers.length > 0) {
      // Filter out the current user and filter by permissions
      const filteredUsers = storeUsers.filter(
        (user) => user.id !== existingUser.id,
      );

      // For "Assign Change Request to" - users with Submit permission
      const submitUsers = filteredUsers.filter(
        (user) => user.permission_to_edit?.Submit === true,
      );

      // For "Reassign Open Tasks To" - users with Reassign permission
      const reassignUsers = filteredUsers.filter(
        (user) => user.permission_to_edit?.Reassign === true,
      );

      setUsersList({
        submitUsers,
        reassignUsers,
        allUsers: filteredUsers,
      });
    }
  }, [storeUsers, existingUser.id]);

  const validatePasswords = (password, confirmPassword) => {
    if (confirmPassword && password && password !== confirmPassword) {
      setPasswordError("Password and confirm password do not match");
    } else if (confirmPassword && password && password === confirmPassword) {
      setPasswordError("");
    } else if (!confirmPassword) {
      setPasswordError("");
    }
  };

  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      setEmailError("");
      return;
    }
    // More strict email validation: local@domain.tld
    // Local part: letters, numbers, dots, hyphens, underscores (no consecutive dots, no leading/trailing dots)
    // Domain: letters, numbers, hyphens, dots (must have valid domain structure)
    // TLD: 2+ letters only, can have subdomains like .co.uk
    // This regex ensures: proper format, no trailing dots, valid TLD
    const emailRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]+([a-zA-Z0-9-]*[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
    const trimmedEmail = email.trim();
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError(
        "Please enter a valid email address (e.g., name@domain.com)",
      );
    } else {
      setEmailError("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent password changes if password fields are disabled
    if (
      (name === "password" || name === "confirmPassword") &&
      shouldDisablePasswordFields
    ) {
      return;
    }

    // Prevent changing status to disabled if user is already disabled (in edit mode)
    if (name === "status" && value === "disabled") {
      if (
        isEditMode &&
        (existingUser.status === "disabled" || existingUser.is_active === false)
      ) {
        return;
      }
    }

    const updatedUserData = { ...userData, [name]: value };
    setUserData(updatedUserData);

    // Validate email in real-time (only for new users)
    if (name === "email" && !isEditMode) {
      validateEmail(value);
    }

    // Validate passwords in real-time (only if password fields are not disabled)
    if (name === "password" && !isEditMode && !shouldDisablePasswordFields) {
      validatePasswords(value, userData.confirmPassword);
    }
  };

  // Helper function to convert role category names to category IDs
  const getRoleCategoryIds = (roleValue) => {
    if (
      !roleValue ||
      roleValue === "None" ||
      roleValue === "" ||
      roleValue === "Select"
    ) {
      return [];
    }

    if (roleValue === "All") {
      // If "All" is selected, include all role category IDs
      return roleCategories.map((cat) => cat.category_id);
    }

    // Parse selected roles (could be string, array, or comma-separated string)
    let selectedRoleNames = [];
    if (Array.isArray(roleValue)) {
      selectedRoleNames = roleValue;
    } else if (typeof roleValue === "string") {
      if (roleValue.includes(",")) {
        selectedRoleNames = roleValue.split(",").map((r) => r.trim());
      } else {
        selectedRoleNames = [roleValue];
      }
    }

    // Map role names to category IDs
    return selectedRoleNames
      .map((roleName) => {
        const category = roleCategories.find(
          (cat) => cat.category_name === roleName,
        );
        return category?.category_id;
      })
      .filter((id) => id !== undefined); // Remove any undefined values
  };

  const handleRoleCheckboxChange = (roleValue) => {
    // Prevent changes if "None" or empty is selected
    if (
      userData.role === "None" ||
      userData.role === "" ||
      userData.role === "All"
    ) {
      return;
    }

    setUserData((prevData) => {
      // Parse current role - could be string, array, or comma-separated string
      let currentRoles = [];
      if (Array.isArray(prevData.role)) {
        currentRoles = [...prevData.role];
      } else if (typeof prevData.role === "string" && prevData.role) {
        // Handle comma-separated string or single value
        if (prevData.role.includes(",")) {
          currentRoles = prevData.role.split(",").map((r) => r.trim());
        } else if (
          prevData.role !== "All" &&
          prevData.role !== "None" &&
          prevData.role !== "Select"
        ) {
          currentRoles = [prevData.role];
        }
      }

      // Toggle the role
      const roleIndex = currentRoles.indexOf(roleValue);
      if (roleIndex > -1) {
        // Remove if already selected
        currentRoles.splice(roleIndex, 1);
      } else {
        // Add if not selected
        currentRoles.push(roleValue);
      }

      // Return as array or comma-separated string based on selection
      // If no roles selected, keep as "Select" to maintain radio button state
      return {
        ...prevData,
        role: currentRoles.length > 0 ? currentRoles.join(", ") : "Select",
      };
    });
  };
  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordChange = (e) => {
    if (shouldDisablePasswordFields) return;
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Check authorization before proceeding
    if (!canManageUsers) {
      showSnackbar("You do not have permission to save users.", "error");
      return;
    }

    // Validate required fields
    if (!isEditMode) {
      // For new users, validate all required fields
      if (!userData.email || !userData.email.trim()) {
        showSnackbar("User Name (Email) is required", "error");
        return;
      }
      // Validate email format
      const emailRegex =
        /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]+([a-zA-Z0-9-]*[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(userData.email.trim())) {
        setEmailError(
          "Please enter a valid email address (e.g., name@domain.com)",
        );
        showSnackbar(
          "Please enter a valid email address (e.g., name@domain.com)",
          "error",
        );
        return;
      }
      // Check if there's an email error from real-time validation
      if (emailError) {
        showSnackbar("Please enter a valid email address", "error");
        return;
      }
      if (!userData.password || !userData.password.trim()) {
        showSnackbar("Password is required", "error");
        return;
      }
      // Validate password confirmation
      if (userData.password !== userData.confirmPassword) {
        showSnackbar("Password and confirm password do not match", "error");
        return;
      }
    }

    // Validate fields required in both create and edit modes
    if (!userData.first_name || !userData.first_name.trim()) {
      showSnackbar("First Name is required", "error");
      return;
    }
    if (!userData.last_name || !userData.last_name.trim()) {
      showSnackbar("Last Name is required", "error");
      return;
    }
    if (!userData.user_information || !userData.user_information.trim()) {
      showSnackbar("User Information is required", "error");
      return;
    }

    // Check if we're trying to disable a user in edit mode
    if (
      isEditMode &&
      userData.status === "disabled" &&
      existingUser.status !== "disabled" &&
      existingUser.is_active !== false
    ) {
      // First check for open tasks (tasks other than COMPLETED and CANCELLED)
      try {
        const tasksResult = await dispatch(
          fetchTasksByUserId({ user_id: existingUser.id }),
        );
        const tasks =
          tasksResult.payload?.results || tasksResult.payload?.data || [];

        // Filter to get only open tasks (not COMPLETED or CANCELLED)
        const openTasks = tasks.filter(
          (task) => task.status !== "COMPLETED" && task.status !== "CANCELLED",
        );

        // If there are open tasks, show the task popup
        if (openTasks.length > 0) {
          setIsDisableTaskPopupOpen(true);
          return;
        }

        // If no open tasks, directly open the disable modal
        setIsDisableModalOpen(true);
        return;
      } catch (error) {
        console.error("Error fetching tasks:", error);
        // On error, still show the disable modal
        setIsDisableModalOpen(true);
        return;
      }
    }

    // Ensure all permissions are included with default values (false if not checked)
    // If permission_to_edit is "All", set all permissions to true
    const allPermissions =
      userData.permission_to_edit === "All"
        ? Object.fromEntries([
          ...permissionCheckboxes.map((perm) => [perm, true]),
          ["Admin", true],
          ["SuperAdmin", true],
          ["Access Backup", true],
          ["Restore Backup", true],
        ])
        : userData.permission_to_edit === "No Access"
          ? Object.fromEntries(
              permissionCheckboxes.map((perm) => [perm, false]),
            )
          : Object.fromEntries(
            permissionCheckboxes.map((perm) => [
              perm,
              perm === "View"
                ? true // View is true when not "No Access"
                : !!userData.permission_to_edit?.[perm],
              ]),
          );

    // Set View to false only if "No Access" is selected, otherwise true
    if (userData.permission_to_edit !== "No Access") {
      allPermissions.View = true;
    }

    // Always include Admin and SuperAdmin (true/false based on checkbox state)
    if (userData.permission_to_edit !== "All") {
      allPermissions.Admin = !!userData.permission_to_edit?.Admin;
      allPermissions.SuperAdmin = !!userData.permission_to_edit?.SuperAdmin;
      // Include Access Backup and Restore Backup based on checkbox state
      allPermissions["Access Backup"] =
        !!userData.permission_to_edit?.["Access Backup"];
      allPermissions["Restore Backup"] =
        !!userData.permission_to_edit?.["Restore Backup"];
    }

    // Track modified fields for audit trail
    const changes = {};
    Object.keys(userData).forEach((key) => {
      if (isEditMode && userData[key] !== existingUser[key]) {
        changes[key] = {
          previous: existingUser[key] || "N/A",
          new: userData[key],
        };
      }
    });
    if (isEditMode) {
      const previousRecordPermissions = {
        ...getInitialRecordPermissions(),
        ...(existingUser.record_permissions || {}),
      };
      if (
        JSON.stringify(recordPermissionsState.selections) !==
        JSON.stringify(previousRecordPermissions)
      ) {
        changes.record_permissions = {
          previous: previousRecordPermissions,
          new: recordPermissionsState.selections,
        };
      }
    }
    setIsLoading(true);
    const hash = await generateSHA256(Math.random().toString());

    // Validate password in edit mode before proceeding
    if (isEditMode && passwordData.newPassword) {
      if (passwordData.newPassword !== passwordData.confirmNewPassword) {
        showSnackbar(
          "New password and confirm new password do not match",
          "error",
        );
        setIsLoading(false);
        return;
      }
    }

    // Determine password value
    let passwordValue = null;
    if (isEditMode) {
      // In edit mode, use newPassword if provided
      passwordValue = passwordData.newPassword?.trim() || null;
    } else {
      // In create mode, use password from userData
      passwordValue = userData.password?.trim() || null;
    }

    // Convert selected role category names to category IDs for user_roles array
    const userRoles = getRoleCategoryIds(userData.role);

    const formattedUserData = {
      ...(isEditMode && { id: existingUser.id }), // Add id only in edit mode
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email,
      // Include password only when it has a value
      ...(passwordValue && { password: passwordValue }),
      status: userData.status,
      user_roles: userRoles, // Array of category IDs (UUIDs)
      user_information: userData.user_information,
      note: userData.note,
      resetpasswordtoken: "abc123xyz",
      resetpasswordexpires: new Date().toISOString(),

      // Filter module_type_permissions to only include modules that exist in the UI
      module_type_permissions: Object.fromEntries(
        Object.entries(userData.module_type_permissions || {}).filter(
          ([moduleName]) => moduleOptions.includes(moduleName),
        ),
      ),

      // Send all edit permissions (true/false based on checkbox)
      permission_to_edit: allPermissions, // ✅ Now includes all permissions
      // Record permissions for live mode
      livemode_permissions_to_edit: recordPermissionsState.selections,
      audit_trail_data: {
        ...auditData,
        hash,
        action: isEditMode ? "UPDATE_USER" : "CREATE_USER",
        comment: isEditMode
          ? `Edited User: ${existingUser.email}`
          : `Created User: ${userData.email}`,
        date: new Date().toISOString(),
        parent_version: isEditMode ? existingUser.version_id_ref : null,
        version_content: {
          rule_name: "Field Changes Tracking",
          modified_fields: changes,
        },
      },
    };

    if(userData.status === "active"){
      formattedUserData.is_active = true
    }

    try {
      let result;
      if (isEditMode) {
        result = await dispatch(updateUser(formattedUserData)).unwrap();
      } else {
        result = await dispatch(registerUser(formattedUserData)).unwrap();
      }

      // Show success message
      showSnackbar(
        `User ${isEditMode ? "updated" : "created"} successfully!`,
        "success",
      );

      setIsLoading(false);

      // Delay navigation to allow user to see success message
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} user:`,
        error,
      );
      // Handle error message - could be a string or object
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.error ||
          error?.message ||
            `Error ${
              isEditMode ? "updating" : "creating"
          } user. Please try again.`;
      showSnackbar(errorMessage, "error");
      setIsLoading(false);
    }
  };

  // Handle Module Permissions (All or Selective)
  const handleModuleSelection = (e) => {
    const { value } = e.target;
    if (value === "all") {
      setModuleLiveMode("all");
      setUserData((prevData) => ({
        ...prevData,
        module_type_permissions: Object.fromEntries(
          moduleOptions.map((mod) => [
            mod,
            {
              view: true,
              edit: prevData.module_type_permissions?.[mod]?.edit || false,
            },
          ]),
        ),
      }));
    } else {
      setModuleLiveMode("select");
      // Clear only view permissions, preserve edit permissions
      setUserData((prevData) => ({
        ...prevData,
        module_type_permissions: Object.fromEntries(
          moduleOptions.map((mod) => [
            mod,
            {
              view: false,
              edit: prevData.module_type_permissions?.[mod]?.edit || false,
            },
          ]),
        ),
      }));
    }
  };
  const handleModuleEditSelection = (e) => {
    const { value } = e.target;
    if (value === "all") {
      setModuleEditMode("all");
      setUserData((prevData) => ({
        ...prevData,
        module_type_permissions: Object.fromEntries(
          moduleOptions.map((mod) => [
            mod,
            {
              edit: true,
              view: prevData.module_type_permissions?.[mod]?.view || false,
            },
          ]),
        ),
      }));
    } else {
      setModuleEditMode("select");
      // Clear only edit permissions, preserve view permissions
      setUserData((prevData) => ({
        ...prevData,
        module_type_permissions: Object.fromEntries(
          moduleOptions.map((mod) => [
            mod,
            {
              edit: false,
              view: prevData.module_type_permissions?.[mod]?.view || false,
            },
          ]),
        ),
      }));
    }
  };

  const handleModulePermissionChange = (module, type) => {
    if (type === "view") {
      setModuleLiveMode("select");
    }
    if (type === "edit") {
      setModuleEditMode("select");
    }
    setUserData((prevData) => ({
      ...prevData,
      module_type_permissions: {
        ...prevData.module_type_permissions,
        [module]: {
          view: prevData.module_type_permissions[module]?.view || false,
          edit: prevData.module_type_permissions[module]?.edit || false,
          [type]: !prevData.module_type_permissions[module]?.[type],
        },
      },
    }));
  };

  // Helper function to check if all permissions are selected
  const areAllPermissionsSelected = (permissions) => {
    if (typeof permissions !== "object" || permissions === null) {
      return permissions === "All";
    }
    return (
      permissionCheckboxes.every((perm) => permissions[perm] === true) &&
      (permissions.Admin === true || permissions.SuperAdmin === true)
    );
  };

  // Helper function to check if we're in "Select Permissions" mode
  const isSelectPermissionsMode = (permissions) => {
    if (permissions === "Select") {
      return true;
    }
    if (typeof permissions !== "object" || permissions === null) {
      return false;
    }
    // If SuperAdmin is true, it's not "Select" mode (it's Admin/SuperAdmin mode)
    if (permissions.SuperAdmin === true) {
      return false;
    }
    // If it's an object (and not SuperAdmin), it's "Select" mode
    return true;
  };

  // Handle Edit Mode Permissions (All or Specific)
  const handleEditModeSelection = (role) => {
    setUserData((prevData) => {
      const prevPermissions =
        typeof prevData.permission_to_edit === "object" &&
          prevData.permission_to_edit !== null
          ? prevData.permission_to_edit
          : {};
      const updatedPermissions = { ...prevPermissions };

      // Handle Admin or SuperAdmin as checkboxes (both can be selected)
      if (role === "Admin" || role === "SuperAdmin") {
        // Toggle the clicked role independently
        const isCurrentlyChecked = prevPermissions[role] === true;
        updatedPermissions[role] = !isCurrentlyChecked;

        if (isCurrentlyChecked) {
          // Unchecking the role

          // If SuperAdmin is being unchecked, clear module and record permissions
          if (role === "SuperAdmin") {
            // Clear all module permissions
            const clearedModulePermissions = Object.fromEntries(
              moduleOptions.map((mod) => [
                mod,
                {
                  edit: false,
                  view: false,
                },
              ]),
            );

            // Reset module modes to "select"
            setModuleLiveMode("select");
            setModuleEditMode("select");

            // Clear all record permissions
            const clearedRecordPermissions = getInitialRecordPermissions();
            setRecordPermissionsState({
              mode: "none",
              selections: clearedRecordPermissions,
            });
          }

          // If Admin is being unchecked, clear permission checkboxes only if SuperAdmin is not checked
          // SuperAdmin has all rights, so if SuperAdmin is still checked, keep all permissions true
          if (role === "Admin") {
            // Only clear permissions if SuperAdmin is also unchecked
            if (!updatedPermissions.SuperAdmin) {
              permissionCheckboxes.forEach((perm) => {
                if (perm !== "View") {
                  updatedPermissions[perm] = false;
                }
              });
              // Remove access-related permissions
              delete updatedPermissions["Access Backup"];
              delete updatedPermissions["Restore Backup"];
            } else {
              // SuperAdmin is still checked, so keep all permissions true
              permissionCheckboxes.forEach((perm) => {
                updatedPermissions[perm] = true;
              });
              updatedPermissions["Access Backup"] = true;
              updatedPermissions["Restore Backup"] = true;
            }
          }

          // Ensure View is always true
          updatedPermissions.View = true;

          // If both Admin and SuperAdmin are now unchecked, clear access permissions
          if (!updatedPermissions.Admin && !updatedPermissions.SuperAdmin) {
            delete updatedPermissions["Access Backup"];
            delete updatedPermissions["Restore Backup"];
          }

          const newData = {
            ...prevData,
            permission_to_edit: updatedPermissions,
            ...(role === "SuperAdmin" && {
              module_type_permissions: Object.fromEntries(
                moduleOptions.map((mod) => [
                  mod,
                  {
                    edit: false,
                    view: false,
                  },
                ]),
              ),
            }),
          };
          return newData;
        } else {
          // Checking the role

          // Set all permission checkboxes to true (View is always true)
          permissionCheckboxes.forEach((perm) => {
            updatedPermissions[perm] = true;
          });
          updatedPermissions.View = true; // Ensure View is always true
          updatedPermissions["Access Backup"] = true;
          updatedPermissions["Restore Backup"] = true;

          // If SuperAdmin is being checked, automatically select all module and record permissions
          if (role === "SuperAdmin") {
            // Set all module permissions (both view and edit) to true for all modules
            const allModulePermissions = Object.fromEntries(
              moduleOptions.map((mod) => [
                mod,
                {
                  view: true,
                  edit: true,
                },
              ]),
            );

            // Set module modes to "all"
            setModuleLiveMode("all");
            setModuleEditMode("all");

            // Set all record permissions to true
            const allRecordPermissions = permissionCheckboxes.reduce(
              (acc, perm) => {
                acc[perm] = true;
                return acc;
              },
              {},
            );

            // Set record permissions state to "all" mode
            setRecordPermissionsState({
              mode: "all",
              selections: allRecordPermissions,
            });

            // SuperAdmin gets all roles automatically
            return {
              ...prevData,
              permission_to_edit: updatedPermissions,
              module_type_permissions: allModulePermissions,
              role: "All", // SuperAdmin automatically gets all roles
            };
          }

          // For Admin, keep module permissions independent (existing behavior)
          return {
            ...prevData,
            permission_to_edit: updatedPermissions, // Keep as object with all permissions true
            // Don't modify module_type_permissions - keep them as they are
          };
        }
      }

      return {
        ...prevData,
        permission_to_edit: updatedPermissions,
      };
    });
  };

  const handlePermissionCheckboxChange = (perm) => {
    // Prevent View from being changed - it's always enabled
    if (perm === "View") {
      return;
    }

    setUserData((prevData) => {
      // Convert string values to object if needed
      let prevPermissions = {};
      if (
        typeof prevData.permission_to_edit === "object" &&
        prevData.permission_to_edit !== null
      ) {
        prevPermissions = { ...prevData.permission_to_edit };
      } else if (prevData.permission_to_edit === "All") {
        // If "All" was selected, initialize all permissions to true
        prevPermissions = permissionCheckboxes.reduce((acc, p) => {
          acc[p] = true;
          return acc;
        }, {});
        prevPermissions.Admin = true;
        prevPermissions.SuperAdmin = false;
        prevPermissions["Access Backup"] = true;
        prevPermissions["Restore Backup"] = true;
      } else {
        // For "No Access" or other cases, start with empty permissions
        prevPermissions = {};
      }

      // Set View based on permission mode (false for "No Access", preserve existing value otherwise)
      const newPermissions = {
        ...prevPermissions,
        View:
          prevData.permission_to_edit === "No Access"
            ? false
            : prevPermissions.View !== undefined
              ? prevPermissions.View
              : true, // Default to true if not explicitly set
        [perm]: !prevPermissions[perm],
      };

      return {
        ...prevData,
        permission_to_edit: newPermissions, // Keep as object to maintain "Select" mode
      };
    });
  };

  const handleRecordPermissionModeChange = (mode) => {
    setRecordPermissionsState((prev) => {
      let selections = prev.selections;
      if (mode === "all") {
        selections = permissionCheckboxes.reduce((acc, perm) => {
          acc[perm] = true;
          return acc;
        }, {});
      } else if (mode === "none") {
        selections = getInitialRecordPermissions();
      } else if (mode === "select") {
        // When switching to "select", ensure View is always true
        selections = {
          ...prev.selections,
          View: true,
        };
      }
      return {
        mode,
        selections,
      };
    });
  };

  const handleRecordPermissionCheckboxToggle = (perm) => {
    // Prevent toggling View - it should always be true when not in "none" mode
    if (perm === "View") {
      return;
    }
    setRecordPermissionsState((prev) => {
      const newSelections = {
        ...prev.selections,
        [perm]: !prev.selections[perm],
        View: true, // Always keep View as true when in select mode
      };
      return {
        mode: "select",
        selections: newSelections,
      };
    });
  };

  const handleDisableSignatureEnd = () => {
    const canvas = sigPadRef.current?.getCanvas();
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      setDisableUserData((prev) => ({
        ...prev,
        signature: dataUrl,
      }));
    }
  };

  // Helper function to check if there are any changes besides status change to disabled
  const hasChangesBesidesStatusDisable = () => {
    // Check basic fields
    if (userData.first_name !== existingUser.first_name) return true;
    if (userData.last_name !== existingUser.last_name) return true;
    if (userData.email !== existingUser.email) return true;
    if (userData.user_information !== existingUser.user_information)
      return true;
    if (userData.note !== existingUser.note) return true;

    // Check password change
    if (passwordData.newPassword && passwordData.newPassword.trim())
      return true;

    // Check role changes
    const currentRole = userData.role || "";
    const existingRoleValue = existingUser.role_categories
      ? existingUser.role_categories
          .map((role) => role.category_name)
          .filter(Boolean)
          .join(", ")
      : existingUser.role || "";

    // Normalize role comparison
    const currentRoleIds = getRoleCategoryIds(currentRole);
    const existingRoleIds = existingUser.role_categories
      ? existingUser.role_categories.map((r) => r.category_id).filter(Boolean)
      : getRoleCategoryIds(existingRoleValue);

    if (
      JSON.stringify(currentRoleIds.sort()) !==
      JSON.stringify(existingRoleIds.sort())
    ) {
      return true;
    }

    // Check module permissions changes
    const currentModulePerms = userData.module_type_permissions || {};
    const existingModulePerms = existingUser.module_type_permissions || {};

    // Compare module permissions
    const allModules = new Set([
      ...Object.keys(currentModulePerms),
      ...Object.keys(existingModulePerms),
    ]);

    for (const module of allModules) {
      if (moduleOptions.includes(module)) {
        const currentPerm = currentModulePerms[module] || {
          view: false,
          edit: false,
        };
        const existingPerm = existingModulePerms[module] || {
          view: false,
          edit: false,
        };

        if (
          currentPerm.view !== existingPerm.view ||
          currentPerm.edit !== existingPerm.edit
        ) {
          return true;
        }
      }
    }

    // Check permission_to_edit changes
    const currentPerms = userData.permission_to_edit;
    const existingPerms = existingUser.permission_to_edit;

    if (JSON.stringify(currentPerms) !== JSON.stringify(existingPerms)) {
      return true;
    }

    // Check record permissions (livemode_permissions_to_edit)
    // Use livemode_permissions_to_edit from existingUser, not record_permissions
    const previousRecordPermissions = {
      ...getInitialRecordPermissions(),
      ...(existingUser.livemode_permissions_to_edit || {}),
    };

    if (
      JSON.stringify(recordPermissionsState.selections) !==
      JSON.stringify(previousRecordPermissions)
    ) {
      return true;
    }

    return false;
  };

  const handleDisableUser = async () => {
    // Validate required fields
    // assignChangeRequestTo and reassignOpenTasksTo are optional fields

    if(!disableUserData.changeRequestComments || disableUserData.changeRequestComments?.length === 0){
      showSnackbar("Comment is required", "error");
      return
    }
    // Only validate password if it's in auth methods
    if (authMethods.includes("password") && !disableUserData.password) {
      showSnackbar("Password is required", "error");
      return;
    }

    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      const isSignatureEmpty = sigPadRef.current?.isEmpty() ?? true;
      if (!disableUserData.signature || isSignatureEmpty) {
        showSnackbar("Signature is required", "error");
        return;
      }
    }

    setIsDisablingUser(true);
    try {
      // Build rev_details from API response if grouped, else aggregate from rows
      let rev_details = [];
      const groups = revisionsByUserData?.data || revisionsByUserData?.results;
      if (
        Array.isArray(groups) &&
        groups.length > 0 &&
        groups[0]?.parts_with_rev !== undefined
      ) {
        rev_details = groups.map((g) => ({
          purpose: g.purpose,
          parts_with_rev: g.parts_with_rev,
          rev_ids: g.rev_ids,
        }));
      } else {
        const purposeAggregation = {};
        for (const proc of proceduresData) {
          const purposeKey = (proc.purpose || "Submit").trim();
          const partLabel = `${proc.part_number || "N/A"} Rev ${
            proc.rev || "N/A"
            }`;
          const revId = proc.id || proc.procedure_revision_ref || "";
          if (!purposeAggregation[purposeKey]) {
            purposeAggregation[purposeKey] = { parts: [], revIds: [] };
          }
          purposeAggregation[purposeKey].parts.push(partLabel);
          if (revId) purposeAggregation[purposeKey].revIds.push(revId);
        }
        rev_details = Object.entries(purposeAggregation).map(
          ([purpose, { parts, revIds }]) => ({
            purpose,
            parts_with_rev: parts.join(", "),
            rev_ids: revIds.join(", "),
          }),
        );
      }

      // Prepare the payload for the API
      const ad = await getAuditTrailData(
        {
          action: "DISABLED_USER",
          comment: disableUserData.changeRequestComments || "User disabled",
          parent_version: existingUser.version_id_ref || "",

          signature: authMethods.includes("signature")
            ? disableUserData.signature
            : "",
          ...(authMethods.includes("password") && disableUserData.password
            ? { hash: disableUserData.password }
            : {}),
          ...(!authMethods.includes("password") && {
            is_password_required: false,
          }),

          password: disableUserData.password,
          version_content: {
            action_to_show: "Disabled a User",
            disabledBy: disableUserData.disabledBy,
            disablingUser: userData.first_name + " " + userData.last_name,
            password_bullets: disableUserData.password?.length,
            ...getFieldsIncluded(),
          },
        },
        false,
      );

      const new_content_snapshot = await uploadJsonToS3(
        ad.version_content,
        "USER",
        "DISABLE",
        true
      );

      ad.version_content = {
        action_to_show:"Disabled a User"
      }
      ad.new_content_snapshot = new_content_snapshot
      const payload = {
        user_id: existingUser.id,
        status: "disabled",
        is_active: false,
        new_assigned: disableUserData.assignChangeRequestTo
          ? [
              {
                purpose: "Submit",
                user_ref: disableUserData.assignChangeRequestTo,
        },
            ]
          : [],
        rev_details,
        audit_trail_data: ad,
      };

      if (!payload.audit_trail_data.parent_version) {
        delete payload.audit_trail_data.parent_version;
      }

      // Call the changeUserStatus API
      const result = await dispatch(changeUserStatus(payload));

      // Check if the API call failed
      if (result.error) {
        console.error("Error disabling user:", result.error);
        showSnackbar("Error disabling user. Please try again.", "error");
        setIsDisablingUser(false);
        return;
      }

      // Only call updateUser API if there are changes besides status change to disabled
      const hasOtherChanges = hasChangesBesidesStatusDisable();

      if (hasOtherChanges) {
        // Also call updateUser API with status: "disabled" and is_active: false
        const currentRole = userData.role || existingUser.role || "";
        const updateUserPayload = {
          id: existingUser.id,
          first_name: userData.first_name || existingUser.first_name,
          last_name: userData.last_name || existingUser.last_name,
          email: userData.email || existingUser.email,
          status: "disabled",
          is_active: false,
          // role: currentRole,
          user_roles: getRoleCategoryIds(currentRole), // Array of category IDs (UUIDs)
          user_information:
            userData.user_information || existingUser.user_information,
          note: userData.note || existingUser.note,
          resetpasswordtoken: "abc123xyz",
          resetpasswordexpires: new Date().toISOString(),
          // Filter module_type_permissions to only include modules that exist in the UI
          module_type_permissions: Object.fromEntries(
            Object.entries(
              userData.module_type_permissions ||
                existingUser.module_type_permissions ||
                {},
            ).filter(([moduleName]) => moduleOptions.includes(moduleName)),
          ),
          permission_to_edit:
            userData.permission_to_edit || existingUser.permission_to_edit,
          livemode_permissions_to_edit: recordPermissionsState.selections,
          audit_trail_data: {
            ...auditData,
            hash: await generateSHA256(Math.random().toString()),
            action: "UPDATE_USER",
            comment: `Disabled User: ${existingUser.email}`,
            date: new Date().toISOString(),
            parent_version: existingUser.version_id_ref || null,
            version_content: {
              rule_name: "Field Changes Tracking",
              modified_fields: {
                status: {
                  previous: existingUser.status || "active",
                  new: "disabled",
                },
                is_active: {
                  previous: existingUser.is_active !== false,
                  new: false,
                },
              },
            },
          },
        };

        // Call updateUser API
        const updateResult = await dispatch(updateUser(updateUserPayload));

        // Check if the updateUser API call failed
        if (updateResult.error) {
          console.error("Error updating user status:", updateResult.error);
          showSnackbar(
            "User disabled but failed to update status. Please try again.",
            "error",
          );
          setIsDisablingUser(false);
          return;
        }
      }

      // Update local state
      setUserData((prev) => ({
        ...prev,
        status: "disabled",
        is_active: false,
      }));
      setIsDisableModalOpen(false);
      setDisableUserData({
        proceduresToChange: [],
        assignChangeRequestTo: "",
        changeRequestComments: "",
        reassignOpenTasksTo: "",
        disabledBy: localStorage.getItem("user_name") || "",
        password: "",
        signature: "",
      });

      showSnackbar("User has been disabled successfully", "success");
      setIsDisablingUser(false);

      // Navigate back after successful disable
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      console.error("Error disabling user:", error);
      showSnackbar("Error disabling user. Please try again.", "error");
      setIsDisablingUser(false);
    }
  };

  return (
    <div>
      <AppBar
        title={
          isEditMode
            ? `User ${userData.first_name} ${userData.last_name}`
            : "New User"
        }
        titleStyle={{ color: "#2269d5" }}
        showTablesDropdown={false}
        showProfileButton={false}
        buttons={[
          {
            label: "Save",
            onClick: canManageUsers ? handleSubmit : undefined,
            disabled: !canManageUsers,
            title: !canManageUsers
              ? "You do not have permission to save users."
              : undefined,
          },
        ]}
      />
      <h3 className="user-form-title">
        {isEditMode ? "Edit User" : "Add New User"}
      </h3>
      <br />

      <form className="user-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            <TextField
              label={`User Name${isEditMode ? " (Read Only)" : ""}`}
              type="email"
              name="email"
              value={userData.email}
              onChange={handleChange}
              onBlur={(e) => {
                if (!isEditMode) {
                  validateEmail(e.target.value);
                }
              }}
              required={!isEditMode}
              readOnly={isEditMode}
              className="form-group"
              inputStyle={{
                borderColor: emailError ? "#dc3545" : undefined,
              }}
            />
            {emailError && !isEditMode && (
              <div
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  position: "absolute",
                  top: "calc(100% + 2px)",
                  left: "10px",
                }}
              >
                {emailError}
              </div>
            )}
          </div>
          <TextField
            label={`First Name${isEditMode ? " (Read Only)" : ""}`}
            type="text"
            name="first_name"
            value={userData.first_name}
            onChange={handleChange}
            required={!isEditMode}
            className="form-group"
            readOnly={isEditMode}
          />
          <TextField
            label={`Last Name${isEditMode ? " (Read Only)" : ""}`}
            type="text"
            name="last_name"
            value={userData.last_name}
            onChange={handleChange}
            required={!isEditMode}
            className="form-group"
            readOnly={isEditMode}
          />
        </div>
        {!isEditMode && (
          <div className="form-row">
            <div className="form-group password-group">
              <label>
                Password<span className="required">*</span>
              </label>
              <div className="password-wrapper">
                <input
                  type={passwordVisibility.password ? "text" : "password"}
                  name="password"
                  value={userData.password || ""}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  style={{
                    borderColor: passwordError ? "#dc3545" : undefined,
                    opacity: shouldDisablePasswordFields ? 0.6 : 1,
                    cursor: shouldDisablePasswordFields
                      ? "not-allowed"
                      : "text",
                  }}
                />
                <span
                  className="toggle-eye"
                  onClick={() =>
                    !shouldDisablePasswordFields &&
                    togglePasswordVisibility("password")
                  }
                  style={{
                    cursor: shouldDisablePasswordFields
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  <FontAwesomeIcon
                    icon={passwordVisibility.password ? faEye : faEyeSlash}
                    style={{ color: "#888" }}
                  />
                </span>
              </div>
            </div>
            <div className="form-group password-group">
              <label>
                Confirm Password<span className="required">*</span>
              </label>
              <div className="password-wrapper">
                <input
                  type={
                    passwordVisibility.confirmPassword ? "text" : "password"
                  }
                  name="confirmPassword"
                  value={userData.confirmPassword || ""}
                  onChange={(e) => {
                    if (shouldDisablePasswordFields) return;
                    const confirmPasswordValue = e.target.value;
                    setUserData({
                      ...userData,
                      confirmPassword: confirmPasswordValue,
                    });
                    // Validate passwords in real-time
                    validatePasswords(userData.password, confirmPasswordValue);
                  }}
                  autoComplete="new-password"
                  style={{
                    borderColor: passwordError ? "#dc3545" : undefined,
                    opacity: shouldDisablePasswordFields ? 0.6 : 1,
                    cursor: shouldDisablePasswordFields
                      ? "not-allowed"
                      : "text",
                  }}
                />
                <span
                  className="toggle-eye"
                  onClick={() =>
                    !shouldDisablePasswordFields &&
                    togglePasswordVisibility("confirmPassword")
                  }
                  style={{
                    cursor: shouldDisablePasswordFields
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      passwordVisibility.confirmPassword ? faEye : faEyeSlash
                    }
                    style={{ color: "#888" }}
                  />
                </span>
              </div>
            </div>
            <div className="form-group password-group">
              <label style={{ visibility: "hidden" }}>
                sronly<span className="required">*</span>
              </label>
              <div
                className="password-wrapper"
                style={{ visibility: "hidden" }}
              >
                <input
                  type={passwordVisibility.password ? "text" : "password"}
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        )}
        {isEditMode && (
          <div className="form-row">
            <div className="form-group password-group">
              <label>Enter New Password</label>
              <div className="password-wrapper">
                <input
                  type={passwordVisibility.newPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  disabled={!canChangePassword}
                  autoComplete="new-password"
                />
                <span
                  className="toggle-eye"
                  onClick={() =>
                    canChangePassword && togglePasswordVisibility("newPassword")
                  }
                  style={{
                    cursor: canChangePassword ? "pointer" : "not-allowed",
                    opacity: canChangePassword ? 1 : 0.5,
                  }}
                >
                  <FontAwesomeIcon
                    icon={passwordVisibility.newPassword ? faEye : faEyeSlash}
                    style={{ color: "#888" }}
                  />
                </span>
              </div>
            </div>
            <div className="form-group password-group">
              <label>Confirm New Password</label>
              <div className="password-wrapper">
                <input
                  type={
                    passwordVisibility.confirmNewPassword ? "text" : "password"
                  }
                  name="confirmNewPassword"
                  value={passwordData.confirmNewPassword}
                  onChange={handlePasswordChange}
                  disabled={!canChangePassword}
                  autoComplete="new-password"
                />
                <span
                  className="toggle-eye"
                  onClick={() =>
                    canChangePassword &&
                    togglePasswordVisibility("confirmNewPassword")
                  }
                  style={{
                    cursor: canChangePassword ? "pointer" : "not-allowed",
                    opacity: canChangePassword ? 1 : 0.5,
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      passwordVisibility.confirmNewPassword ? faEye : faEyeSlash
                    }
                    style={{ color: "#888" }}
                  />
                </span>
              </div>
            </div>
            <div className="form-group password-group">
              <label style={{ visibility: "hidden" }}>
                sronly<span className="required">*</span>
              </label>
              <div
                className="password-wrapper"
                style={{ visibility: "hidden" }}
              >
                <input
                  type={passwordVisibility.password ? "text" : "password"}
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-group inline-permissions">
          <label className="permission-label">
            Module Permissions (Live Mode)
          </label>

          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                minWidth: "110px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
                marginLeft: "5px",
              }}
            >
              <RadioButton
                name="moduleSelectLive"
                value="all"
                onChange={handleModuleSelection}
                checked={moduleLiveMode === "all"}
                label={
                  <span
                    style={{
                      marginTop: "4px",
                    }}
                  >
                    All Modules
                  </span>
                }
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                minWidth: "110px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="moduleSelectLive"
                value="select"
                onChange={handleModuleSelection}
                checked={moduleLiveMode === "select"}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label={<span style={{ marginTop: "4px" }}>Select Modules</span>}
              />
            </div>

            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>

            {moduleOptions.map((module, index) => (
              <div
                key={module}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: "110px",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                  marginRight:
                    module === "Not Assigned"
                      ? "10px" // Not Assigned - proper spacing
                      : module === "Product"
                        ? "-20px" // Product
                        : module === "Document"
                          ? "5px" // Document - reduced spacing
                          : module === "Procedure"
                            ? "-17px" // Procedure
                            : "10px", // Default (QMS or other modules)
                  marginLeft: module === "Not Assigned" ? "17px" : undefined, // Add left margin to Not Assigned to offset Procedure's negative margin
                }}
              >
                <InlineCheckbox
                  checked={!!userData.module_type_permissions[module]?.view}
                  onChange={() => handleModulePermissionChange(module, "view")}
                  disabled={moduleLiveMode === "all"}
                  label={module}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="form-group inline-permissions">
          <label className="permission-label">
            Module Permissions (Edit Mode)
          </label>

          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                minWidth: "110px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
                marginLeft: "5px",
              }}
            >
              <RadioButton
                name="moduleSelectEdit"
                value="all"
                onChange={handleModuleEditSelection}
                checked={moduleEditMode === "all"}
                label={
                  <span
                    style={{
                      marginTop: "4px",
                    }}
                  >
                    All Modules
                  </span>
                }
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                minWidth: "110px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="moduleSelectEdit"
                value="select"
                onChange={handleModuleEditSelection}
                checked={moduleEditMode === "select"}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label={<span style={{ marginTop: "4px" }}>Select Modules</span>}
              />
            </div>

            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>

            {moduleOptions.map((module, index) => (
              <div
                key={module}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: "110px",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                  marginRight:
                    module === "Not Assigned"
                      ? "10px" // Not Assigned - proper spacing
                      : module === "Product"
                        ? "-20px" // Product
                        : module === "Document"
                          ? "5px" // Document - reduced spacing
                          : module === "Procedure"
                            ? "-17px" // Procedure
                            : "10px", // Default (QMS or other modules)
                  marginLeft: module === "Not Assigned" ? "17px" : undefined, // Add left margin to Not Assigned to offset Procedure's negative margin
                }}
              >
                <InlineCheckbox
                  checked={!!userData.module_type_permissions[module]?.edit}
                  onChange={() => handleModulePermissionChange(module, "edit")}
                  disabled={moduleEditMode === "all"}
                  label={module}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group inline-permissions">
          <label className="permission-label">
            Record Permissions in Live Mode
          </label>

          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "70px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="recordPermissions"
                value="none"
                checked={recordPermissionsState.mode === "none"}
                onChange={() => handleRecordPermissionModeChange("none")}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label={
                  <span
                    style={{
                      marginTop: "3px",
                    }}
                  >
                    None
                  </span>
                }
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "70px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="recordPermissions"
                value="all"
                style={{
                  marginTop: "3px",
                }}
                checked={recordPermissionsState.mode === "all"}
                onChange={() => handleRecordPermissionModeChange("all")}
                label={
                  <span
                    style={{
                      marginTop: "3px",
                    }}
                  >
                    All
                  </span>
                }
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "70px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="recordPermissions"
                value="select"
                checked={recordPermissionsState.mode === "select"}
                onChange={() => handleRecordPermissionModeChange("select")}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label={
                  <span
                    style={{
                      marginTop: "3px",
                    }}
                  >
                    Select
                  </span>
                }
              />
            </div>

            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>

            {permissionCheckboxes.map((perm) => (
              <div
                key={perm}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: "85px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                }}
              >
                <InlineCheckbox
                  value={perm}
                  label={perm}
                  checked={
                    perm === "View"
                      ? recordPermissionsState.mode !== "none"
                      : !!recordPermissionsState.selections[perm]
                  }
                  onChange={() => handleRecordPermissionCheckboxToggle(perm)}
                  disabled={
                    perm === "View"
                      ? true // View is always disabled (cannot be toggled)
                      : recordPermissionsState.mode === "none" ||
                      recordPermissionsState.mode === "all"
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group inline-permissions">
          <label className="permission-label">Edit Mode Permission</label>

          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "85px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <InlineCheckbox
                value="Admin"
                checked={!!userData.permission_to_edit?.Admin}
                onChange={() => handleEditModeSelection("Admin")}
                label={<span style={{ marginTop: "3px" }}>Admin</span>}
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "95px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <InlineCheckbox
                value="SuperAdmin"
                checked={!!userData.permission_to_edit?.SuperAdmin}
                onChange={() => handleEditModeSelection("SuperAdmin")}
                label={<span style={{ marginTop: "3px" }}>Superadmin</span>}
              />
            </div>

            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "110px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
                color: "#888888",
              }}
            >
              <InlineCheckbox
                value="Access Backup"
                disabled={
                  (!userData.permission_to_edit?.Admin &&
                    !userData.permission_to_edit?.SuperAdmin) ||
                  userData.permission_to_edit?.SuperAdmin === true
                }
                checked={!!userData.permission_to_edit?.["Access Backup"]}
                onChange={() => handlePermissionCheckboxChange("Access Backup")}
                label={<span style={{ marginTop: "3px" }}>Access Backup</span>}
              />
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "115px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
                color: "#888888",
              }}
            >
              <InlineCheckbox
                value="Restore Backup"
                disabled={
                  (!userData.permission_to_edit?.Admin &&
                    !userData.permission_to_edit?.SuperAdmin) ||
                  userData.permission_to_edit?.SuperAdmin === true
                }
                checked={!!userData.permission_to_edit?.["Restore Backup"]}
                onChange={() =>
                  handlePermissionCheckboxChange("Restore Backup")
                }
                label={<span style={{ marginTop: "3px" }}>Restore Backup</span>}
              />
            </div>
          </div>
        </div>

        <div className="form-group inline-permissions">
          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              width: "84%",
            }}
          >
            <div
              style={{
                display: "flex",
                width: "100%",
                marginBottom: "5px",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: "85px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                }}
              >
                <RadioButton
                  name="editModePermission2"
                  value="noAccess"
                  onChange={() =>
                    setUserData({
                      ...userData,
                      permission_to_edit: "No Access",
                    })
                  }
                  checked={userData.permission_to_edit === "No Access"}
                  disabled={userData.permission_to_edit?.SuperAdmin === true}
                  label="No Access"
                />
              </div>

              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: "170px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                }}
              >
                <RadioButton
                  name="editModePermission2"
                  value="allPermissions"
                  onChange={() =>
                    setUserData({
                      ...userData,
                      permission_to_edit: "All",
                    })
                  }
                  checked={
                    userData.permission_to_edit === "All" ||
                    areAllPermissionsSelected(userData.permission_to_edit)
                  }
                  disabled={userData.permission_to_edit?.SuperAdmin === true}
                  label="All Edit Mode Permissions"
                />
              </div>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "130px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="editModePermission2"
                value="selectPermissions"
                onChange={() => {
                  // Initialize permissions object when "Select Permissions" is clicked
                  let initialPermissions = {};
                  if (
                    typeof userData.permission_to_edit === "object" &&
                    userData.permission_to_edit !== null
                  ) {
                    // Preserve existing permissions
                    initialPermissions = { ...userData.permission_to_edit };
                  } else if (userData.permission_to_edit === "All") {
                    // If "All" was selected, initialize all permission checkboxes to true
                    // But do NOT set Admin, SuperAdmin, Access Backup, or Restore Backup
                    // These should remain unchecked unless explicitly selected
                    initialPermissions = permissionCheckboxes.reduce(
                      (acc, p) => {
                        acc[p] = true;
                        return acc;
                      },
                      {},
                    );
                    // Admin, SuperAdmin, Access Backup, and Restore Backup remain false/unset
                  } else if (userData.permission_to_edit === "No Access") {
                    // If "No Access" was selected, initialize all permissions to false
                    // But View should default to true when switching to "Select Permissions"
                    initialPermissions = permissionCheckboxes.reduce(
                      (acc, p) => {
                        acc[p] = p === "View" ? true : false; // View defaults to true
                        return acc;
                      },
                      {},
                    );
                  }
                  // For other cases, start with empty permissions object
                  setUserData({
                    ...userData,
                    permission_to_edit: initialPermissions,
                  });
                }}
                checked={isSelectPermissionsMode(userData.permission_to_edit)}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label="Select Permissions"
              />
            </div>

            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>

            {permissionCheckboxes.map((perm) => (
              <div
                key={perm}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: "85px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                }}
              >
                <InlineCheckbox
                  value={perm}
                  checked={
                    perm === "View"
                      ? userData.permission_to_edit === "No Access"
                        ? false // View is unchecked when No Access is selected
                        : typeof userData.permission_to_edit === "object" &&
                          userData.permission_to_edit !== null
                          ? (userData.permission_to_edit.View ?? true) // Use View value or default to true
                          : true // View is checked in "All" mode
                      : !!userData.permission_to_edit?.[perm] ||
                      userData.permission_to_edit === "All"
                  }
                  onChange={() => handlePermissionCheckboxChange(perm)}
                  disabled={
                    perm === "View"
                      ? true // View is always disabled (cannot be toggled)
                      : userData.permission_to_edit === "No Access" ||
                      userData.permission_to_edit?.SuperAdmin === true
                  }
                  label={perm}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group inline-permissions">
          <label className="permission-label">Role</label>
          <div
            className="permissions-controls"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              padding: "10px 15px 5px 5px",
              border: "1px solid #ddd",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "85px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="role"
                value="All"
                checked={
                  userData.role === "All" ||
                  userData.permission_to_edit?.SuperAdmin === true
                }
                onChange={(e) => {
                  setUserData((prev) => ({
                    ...prev,
                    role: "All",
                  }));
                }}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label="All"
              />
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "85px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="role"
                value="None"
                checked={userData.role === "None" || userData.role === ""}
                onChange={(e) => {
                  setUserData((prev) => ({
                    ...prev,
                    role: "None",
                  }));
                }}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label="None"
              />
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: "85px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="role"
                value="Select"
                checked={
                  userData.role !== "All" &&
                  userData.role !== "None" &&
                  (userData.role === "Select" ||
                    userData.role === "" ||
                    (typeof userData.role === "string" &&
                      (userData.role.includes(",") ||
                        roleCategories.some(
                          (cat) => userData.role === cat.category_name,
                        ))) ||
                    Array.isArray(userData.role))
                }
                onChange={(e) => {
                  setUserData((prev) => ({
                    ...prev,
                    role: "Select",
                  }));
                }}
                disabled={userData.permission_to_edit?.SuperAdmin === true}
                label="Select"
              />
            </div>
            <span
              className="separator"
              style={{
                margin: "0 8px",
                height: "25px",
                display: "inline-block",
                borderLeft: "3px solid #ccc",
              }}
            ></span>
            {roleCategories.map((category) => (
              <div
                key={category.category_id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: "85px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  fontWeight: "normal",
                }}
              >
                <InlineCheckbox
                  value={category.category_name}
                  checked={
                    userData.role === "All" ||
                    (Array.isArray(userData.role)
                      ? userData.role.includes(category.category_name)
                      : typeof userData.role === "string" &&
                      userData.role.includes(category.category_name))
                  }
                  onChange={() =>
                    handleRoleCheckboxChange(category.category_name)
                  }
                  disabled={
                    userData.permission_to_edit?.SuperAdmin === true ||
                    userData.role === "All" ||
                    userData.role === "None" ||
                    userData.role === ""
                  }
                  label={category.category_name}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group inline-status">
          <label className="status-label">Active Status</label>
          <div className="permissions-controls">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="status"
                value="active"
                checked={userData.status === "active"}
                onChange={handleChange}
                label="Active"
              />
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontWeight: "normal",
              }}
            >
              <RadioButton
                name="status"
                value="disabled"
                checked={userData.status === "disabled"}
                onChange={handleChange}
                disabled={
                  !isEditMode || // Disable in add mode
                  (isEditMode &&
                    (existingUser.status === "disabled" ||
                      existingUser.is_active === false))
                }
                label="Disabled"
              />
            </div>
          </div>
        </div>

        <TextArea
          label={
            <>
              User Information<span className="required">*</span>{" "}
              <span>
                (Shown in Pop-Up When Name of User with Underline is Clicked)
              </span>
            </>
          }
          name="user_information"
          placeholder="Enter user information..."
          value={userData.user_information}
          onChange={handleChange}
          className="form-group"
          labelStyle={{ fontWeight: "bold" }}
        />

        <TextArea
          label="Notes About User Only Visible On This Page"
          name="note"
          placeholder="Enter notes about user..."
          value={userData.note}
          onChange={handleChange}
          className="form-group"
          labelStyle={{ fontWeight: "bold" }}
        />

        <br />
        <br />
      </form>

      {/* Disable User Task Popup */}
      {isDisableTaskPopupOpen && existingUser.id && (
        <DisableUserTaskPopup
          closeModal={(close) => {
            setIsDisableTaskPopupOpen(false);
            // If user closes without continuing, reset status
            if (close) {
              setUserData((prev) => ({
                ...prev,
                status: existingUser.status || "active",
              }));
            }
          }}
          userId={existingUser.id}
          onContinue={() => {
            // Close task popup and open disable modal
            setIsDisableTaskPopupOpen(false);
            setIsDisableModalOpen(true);
          }}
        />
      )}

      {/* Disable User Modal */}
      {isDisableModalOpen && (
        <div className="disable-modal-overlay">
          <div className="disable-modal-content">
            <div className="disable-modal-header-container">
              <h3 className="disable-modal-title">
                <span>
                  User {userData.first_name} {userData.last_name} will be
                  Disabled
                </span>
              </h3>
              <button
                onClick={() => {
                  setIsDisableModalOpen(false);
                }}
                className="disable-modal-close-button"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} size="1x" color="gray" />
              </button>
            </div>

            <hr className="divider" />

            {/* <div className="disable-modal-form-group">
              <label className="disable-modal-label">
                The Following Procedures Will Be Changed to Change Request:
              </label>
              <div>
                {approvedPartsList.length > 0 ? (
                  <span className="disable-modal-procedures-text">
                    {approvedPartsList.join(", ")}
                  </span>
                ) : (
                  <span className="disable-modal-loading-text">
                    {revisionsLoading
                      ? "Loading procedures..."
                      : "No procedures found."}
                  </span>
                )}
              </div>
            </div> */}

            {/* <div className="disable-modal-form-group">
              <label className="disable-modal-label">
                Assign Change Request to:
                <span className="required">*</span>
              </label>
              <div
                className="disable-modal-user-selection custom-scrollbar"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: "15px",
                  padding: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                {usersList.submitUsers && usersList.submitUsers.length > 0 ? (
                  usersList.submitUsers.map((user) => (
                    <label key={user.id} className="disable-modal-user-label">
                      <RadioButton
                        name="assignChangeRequestTo"
                        value={user.id}
                        checked={
                          disableUserData.assignChangeRequestTo === user.id
                        }
                        onChange={(e) =>
                          setDisableUserData((prev) => ({
                            ...prev,
                            assignChangeRequestTo: e.target.value,
                          }))
                        }
                        label={`${user.first_name} ${user.last_name}`}
                      />
                    </label>
                  ))
                ) : (
                  <span className="disable-modal-loading-text">
                    {usersList.submitUsers
                      ? "No users with Submit permission found"
                      : "Loading users..."}
                  </span>
                )}
              </div>
            </div> */}

            <div className="disable-modal-form-group">
              <TextArea
                label={<>Comments<span className="required">*</span></>}
                placeholder="Enter comments..."
                value={disableUserData.changeRequestComments}
                onChange={(e) =>
                  setDisableUserData((prev) => ({
                    ...prev,
                    changeRequestComments: e.target.value,
                  }))
                }
                labelStyle={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              />
            </div>

            {/* <div className="disable-modal-form-group">
              <label className="disable-modal-label">
                Reassign Open Tasks To:
                <span className="required">*</span>
              </label>
              <div
                className="disable-modal-user-selection custom-scrollbar"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: "15px",
                  padding: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                {usersList.reassignUsers &&
                usersList.reassignUsers.length > 0 ? (
                  usersList.reassignUsers.map((user) => (
                    <label key={user.id} className="disable-modal-user-label">
                      <RadioButton
                        name="reassignOpenTasksTo"
                        value={user.id}
                        checked={
                          disableUserData.reassignOpenTasksTo === user.id
                        }
                        onChange={(e) =>
                          setDisableUserData((prev) => ({
                            ...prev,
                            reassignOpenTasksTo: e.target.value,
                          }))
                        }
                        label={`${user.first_name} ${user.last_name}`}
                      />
                    </label>
                  ))
                ) : (
                  <span className="disable-modal-loading-text">
                    {usersList.reassignUsers
                      ? "No users with Reassign permission found"
                      : "Loading users..."}
                  </span>
                )}
              </div>
            </div> */}

            <div className="disable-modal-form-group">
              <div className="disable-modal-disabled-by">
                <label className="disable-modal-disabled-by-label">
                  Disabled By:
                </label>
                <span className="disable-modal-disabled-by-text">
                  {disableUserData.disabledBy}
                </span>
              </div>
            </div>

            {/* Password - Only show if password is in auth methods */}
            {authMethods.includes("password") && (
              <div className="disable-modal-form-group">
                <TextField
                  label={
                    <>
                      Enter Password<span className="required">*</span>
                    </>
                  }
                  type="password"
                  placeholder="Enter your password"
                  value={disableUserData.password}
                  onChange={(e) =>
                    setDisableUserData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                />
              </div>
            )}

            {/* Signature - Only show if signature is in auth methods */}
            {authMethods.includes("signature") && (
              <div className="disable-modal-form-group">
                <SignatureField
                  ref={sigPadRef}
                  label={
                    <>
                      Sign Here<span className="required">*</span>
                    </>
                  }
                  width="100%"
                  height="100px"
                  onEnd={handleDisableSignatureEnd}
                  labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                />
              </div>
            )}

            <div className="disable-modal-footer">
              <button
                onClick={() => {
                  setIsDisableModalOpen(false);
                }}
                className="disable-modal-close-btn"
              >
                Close
              </button>
              <button
                onClick={handleDisableUser}
                className="disable-modal-disable-btn"
              >
                Disable User
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <Loader text={isEditMode ? "Updating user..." : "Creating user..."} />
      )}

      {isDisablingUser && <Loader text="Disabling user..." />}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default UserForm;
